import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { actionOutcome, apiFetch } from '../api';
import usePolling from '../utils/usePolling';
import Modal from '../components/Modal';
import {
  Badge, EmptyState, Icon, PageHeader, Pagination, SearchInput, StatusBadge, TableSkeleton,
} from '../components/ui';

/* `EditUserModal` used to live here: a ~320-line duplicate of the Edit User page, defined
   and never rendered (the row action navigates to /users/:id/edit). It had drifted badly
   enough that wiring it up would have introduced bugs, it posted `memory_limit: '2G'`, a
   field no request model has, and seeded `cpu_limit` from `user.cpu_limit || '1.0'`, so
   saving any unrelated edit would have capped an uncapped user at 1.0 vCPU. Removed rather
   than repaired: the page it duplicates is the one in use. */

const PAGE_SIZE = 10;

// 2-state lifecycle: not_paired → paired. The backend emits `state` from is_paired; the
// older `is_paired` boolean is the fallback.
const pairState = (user) => user.state || (user.is_paired !== false ? 'paired' : 'not_paired');

// Short labels: three channels have to fit one line of a nine-column table.
const CHANNEL_LABEL = {
  telegram: 'Telegram', website: 'Web', teams: 'Teams', rossapp: 'App', 'rossapp-pen': 'App (pentest)',
  voice: 'Phone', whatsapp: 'WhatsApp',
};
const channelLabel = (c) => CHANNEL_LABEL[c] || (c.charAt(0).toUpperCase() + c.slice(1));

// "19 Jul 2026": a month name cannot be misread as D/M vs M/D, and it is narrower than a
// full numeric date plus the words around it. The full timestamp stays in the tooltip.
const formatDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : null);

/* ── Users Page ── */
function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [favorites, setFavorites] = useState(new Set());
  const [actionLoading, setActionLoading] = useState({});
  // The analyst role reads this page and changes nothing: no favourites, no New user, and
  // View and Logs for the only row actions. The backend refuses the rest anyway
  // (api/analyst_guard.py), and leaves the coupon code out of what it sends and searches.
  const readOnly = localStorage.getItem('admin_role') === 'analyst';
  const searchCovers = readOnly ? 'name, ID, email, tier and source' : 'name, ID, email, tier, source and coupon';

  // Fetch favorites on load. Not for an analyst: the read creates the operator's admins
  // row (api/routes/favorites.py), and the guard refuses it.
  useEffect(() => {
    if (readOnly) return;
    apiFetch('/api/favorites')
      .then(r => r.ok ? r.json() : { favorites: [] })
      .then(data => setFavorites(new Set(data.favorites || [])))
      .catch(() => {});
  }, [readOnly]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1); // Reset to first page on search change
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (searchDebounced) params.set('search', searchDebounced);
      const response = await apiFetch(`/api/users?${params}`);
      const data = await response.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
      setLoading(false);
    }
  };

  // Fetch users when page or search changes, then keep the list live every 5s while the
  // tab is visible (utils/usePolling).
  usePolling(fetchUsers, 5000, [page, searchDebounced]);

  const toggleFavorite = async (userId) => {
    const isFav = favorites.has(userId);
    try {
      const resp = await apiFetch(`/api/favorites/${userId}`, { method: isFav ? 'DELETE' : 'POST' });
      if (resp.ok) {
        setFavorites(prev => {
          const next = new Set(prev);
          if (isFav) next.delete(userId);
          else next.add(userId);
          return next;
        });
        // Starred users are ordered first BY THE SERVER (the list is paginated there, so it
        // has to be). Re-fetch so the row moves now instead of on the next 5s poll — without
        // this the star fills in but nothing appears to happen for several seconds.
        fetchUsers();
      } else {
        toast.error(isFav ? 'Failed to remove from favourites' : 'Failed to add to favourites');
      }
    } catch (e) {
      toast.error('Failed to update favourites');
    }
  };

  // `deleting` is true while the DELETE request is in flight. It drives the spinner on
  // the dialog so the admin can neither double-submit nor dismiss it mid-request.
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (deleteData) => {
    if (!deleteTarget) return;
    const { userId, username } = deleteTarget;
    setDeleting(true);
    try {
      const response = await apiFetch(`/api/users/${userId}?delete_data=${deleteData}`, { method: 'DELETE' });
      const { ok, message } = await actionOutcome(response, 'Failed to delete user');
      if (ok) {
        toast.success(deleteData
          ? `User ${username} permanently deleted`
          : `User ${username} soft deleted`);
      } else {
        toast.error(message);
      }
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleContainerAction = async (userId, action) => {
    setActionLoading(prev => ({ ...prev, [userId]: action }));
    try {
      const response = await apiFetch(`/api/containers/${userId}/${action}`, { method: 'POST' });
      // The status alone is not enough: a refused Stop used to come back as a 200 carrying
      // `success: false`, and showed as done (api.js).
      const { ok, message } = await actionOutcome(response, `Agent ${action} failed`);
      if (ok) {
        toast.success(message || `Agent ${action} successful`);
      } else {
        toast.error(message);
      }
      fetchUsers();
    } catch (error) {
      toast.error(`Failed to ${action} agent`);
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const header = (
    <PageHeader
      description={`Everyone on the platform, with the state of their agent. Search covers ${searchCovers}.`}
      actions={readOnly ? null : (
        <Link to="/users/create" className="btn btn-primary">
          <Icon name="userPlus" /> New user
        </Link>
      )}
    >
      <h1 className="page-title">
        Users
        {!loading && <Badge tone="neutral" size="lg">{total}</Badge>}
      </h1>
    </PageHeader>
  );

  return (
    <div>
      {header}
      <div className="card card-flush">
        <div className="toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={readOnly ? 'Search name, ID, email, tier, source…' : 'Search name, ID, email, tier, source, coupon…'}
            label="Search users"
          />
          <div className="toolbar-spacer" />
          {/* The list re-polls every 5s; saying so explains why a row changes under the
              cursor, which otherwise reads as a glitch. */}
          <Badge tone="ok" dot pulse>Live</Badge>
        </div>

        {loading ? (
          <TableSkeleton rows={PAGE_SIZE} cols={7} label="Loading users" />
        ) : users.length === 0 && !searchDebounced ? (
          <EmptyState
            icon="users"
            title="No users yet"
            action={readOnly ? null : <Link to="/users/create" className="btn btn-primary">Create the first user</Link>}
          >
            Users arrive through coupons, Stripe checkout and the producer API{readOnly ? '.' : ', or can be created here.'}
          </EmptyState>
        ) : users.length === 0 && searchDebounced ? (
          <EmptyState
            icon="search"
            title={`No users match “${searchDebounced}”`}
            action={<button type="button" className="btn btn-secondary" onClick={() => setSearch('')}>Clear search</button>}
          >
            Search matches {readOnly ? 'name, ID, email, tier and source' : 'name, ID, email, tier, source and coupon code'}.
          </EmptyState>
        ) : (
          <>
            <div className="table-container">
              <table className="table users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Status</th>
                    <th>Tier</th>
                    <th>Channels</th>
                    <th>Agent</th>
                    <th>Source</th>
                    <th>Created / expires</th>
                    <th><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isFav = favorites.has(user.id);
                    const paired = pairState(user) === 'paired';
                    const channels = user.channels || [];
                    return (
                      <tr key={user.id}>
                        <td data-label="User">
                          <div className="row gap-2">
                            {!readOnly && (
                              <button
                                type="button"
                                className={`fav-btn${isFav ? ' is-on' : ''}`}
                                onClick={(e) => { e.stopPropagation(); toggleFavorite(user.id); }}
                                aria-pressed={isFav}
                                aria-label={isFav ? `Remove ${user.username} from favourites` : `Add ${user.username} to favourites`}
                              >
                                <Icon name="star" size={16} />
                              </button>
                            )}
                            {/* max-width on a <td> is ignored by table layout; on a block inside
                                it, it holds, so a long email truncates instead of widening the
                                column and pushing the actions off screen. */}
                            <div style={{ maxWidth: 200, minWidth: 0 }}>
                              <Link to={`/users/${user.id}`} className="cell-primary">{user.username}</Link>
                              <span className="cell-sub truncate" title={user.email || undefined}>
                                #{user.id}{user.email ? ` · ${user.email}` : ''}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td data-label="Status">
                          <div className="status-lines">
                            <span className={user.is_active ? 'text-ok' : 'text-danger'}>
                              <span className="dot" aria-hidden="true" />{user.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className={paired ? 'text-muted' : 'text-warn'}>
                              <span className="dot" aria-hidden="true" />{paired ? 'Paired' : 'Not paired'}
                            </span>
                          </div>
                        </td>
                        <td data-label="Tier">
                          {user.tier_name
                            ? <Badge tone="accent">{user.tier_name}</Badge>
                            : <span className="text-faint">No tier</span>}
                        </td>
                        {/* Every live channel, not just Telegram. This read `telegram_id ?
                          * 'Telegram' : '-'`, so a user reachable only on the website showed a
                          * dash and looked unpaired. `channels` comes from channel_identities,
                          * which is the table the inbound path actually resolves against. */}
                        <td data-label="Channels">
                          {channels.length ? (
                            <div className="row gap-1 nowrap">
                              {channels.slice(0, 2).map(c => <Badge key={c} tone="outline">{channelLabel(c)}</Badge>)}
                              {channels.length > 2 && (
                                <Badge tone="neutral" title={channels.map(channelLabel).join(', ')}>
                                  +{channels.length - 2}
                                </Badge>
                              )}
                            </div>
                          ) : <span className="text-faint">None</span>}
                        </td>
                        <td data-label="Agent">
                          <StatusBadge status={user.container_status} />
                        </td>
                        <td data-label="Source">
                          <div className="stack gap-1" style={{ alignItems: 'flex-start' }}>
                            <Badge tone={user.source === 'stripe' ? 'violet' : 'neutral'}>{user.source || 'manual'}</Badge>
                            {/* Which coupon brought this user in. 'legacy' is the backfill
                                stamp for redemptions that predate migration 009. Not for an
                                analyst, whom the backend does not send it. */}
                            {!readOnly && user.source === 'coupon' && user.activation_coupon_code && (
                              <span className="mono text-xs text-muted truncate" style={{ maxWidth: 120 }}
                                    title={user.activation_coupon_code}>
                                {user.activation_coupon_code}
                              </span>
                            )}
                          </div>
                        </td>
                        <td data-label="Created / expires" className="nowrap">
                          <span className="num">{formatDate(user.created_at) || '—'}</span>
                          {/* Coupon-driven expiry only — Stripe / manual users have no snapshot
                              expiry. Once the daily lifecycle sweep has stopped the user the
                              date is struck through, so the row reads "expired, already
                              actioned" at a glance. */}
                          {user.source === 'coupon' && user.coupon_expires_at && (
                            <span
                              className={`cell-sub num${user.coupon_stopped_at ? ' strike' : ''}`}
                              title={user.coupon_stopped_at
                                ? `Stopped on ${new Date(user.coupon_stopped_at).toLocaleString()}`
                                : new Date(user.coupon_expires_at).toLocaleString()}
                            >
                              exp. {formatDate(user.coupon_expires_at)}
                            </span>
                          )}
                        </td>
                        {/* The fourth button is Stop, Stopping or Start depending on the agent's
                            state (three widths), and for any OTHER state it is not rendered,
                            so as a flex row Delete jumped a whole button-width left on those
                            rows. Five grid columns: the three constant-width buttons, one `1fr`
                            slot for the state action, then Delete. A table column shares one
                            width across all rows, so `1fr` resolves to the widest state button
                            in EVERY row and all five columns align. The empty span holds the
                            slot when there is no state action.

                            An admin's row opens with Instructions, the agent's system prompt,
                            which is not an analyst's page (the backend refuses them that read).
                            An analyst's row has Logs and View, where an admin's has Edit, so it
                            is a plain row: the grid's empty tracks would leave gaps beside them. */}
                        <td data-label="Actions">
                          <div className={readOnly ? 'row gap-1 nowrap' : undefined} style={readOnly ? undefined : {
                            display: 'grid',
                            gridTemplateColumns: 'auto auto auto 1fr auto',
                            gap: 'var(--sp-1)',
                            alignItems: 'center',
                          }}>
                            {!readOnly && (
                              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => navigate(`/users/${user.id}/instructions`)}
                                      aria-label={`Instructions for ${user.username}`} title="Instructions">
                                <Icon name="instructions" />
                              </button>
                            )}
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => navigate(`/users/${user.id}/logs`)}
                                    aria-label={`Logs for ${user.username}`} title="Logs">
                              <Icon name="terminal" />
                            </button>
                            {readOnly ? (
                              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => navigate(`/users/${user.id}`)}
                                      aria-label={`View ${user.username}`} title="View">
                                <Icon name="eye" />
                              </button>
                            ) : (
                              <>
                                <button className="btn btn-ghost btn-sm btn-icon" onClick={() => navigate(`/users/${user.id}/edit`)}
                                        aria-label={`Edit ${user.username}`} title="Edit">
                                  <Icon name="edit" />
                                </button>
                                {['running', 'starting', 'hibernated'].includes(user.container_status) ? (
                                  <button className="btn btn-secondary btn-sm" onClick={() => handleContainerAction(user.id, 'stop')} disabled={!!actionLoading[user.id]}>
                                    {actionLoading[user.id] === 'stop' ? <span className="spinner-sm" /> : <Icon name="stop" />} Stop
                                  </button>
                                ) : user.container_status === 'stopping' ? (
                                  <button className="btn btn-secondary btn-sm" disabled><span className="spinner-sm" /> Stopping</button>
                                ) : ['stopped', 'pending'].includes(user.container_status) ? (
                                  // 'pending' has never run: its first message would start it,
                                  // and Start does the same now.
                                  <button className="btn btn-success btn-sm" onClick={() => handleContainerAction(user.id, 'start')} disabled={!!actionLoading[user.id]}>
                                    {actionLoading[user.id] === 'start' ? <span className="spinner-sm" /> : <Icon name="play" />} Start
                                  </button>
                                ) : <span />}
                                <button className="btn btn-danger-soft btn-sm btn-icon" aria-label={`Delete ${user.username}`}
                                        title="Delete user"
                                        onClick={() => setDeleteTarget({ userId: user.id, username: user.username })}>
                                  <Icon name="trash" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} noun="users" />
          </>
        )}
      </div>

      {/* This used to ask TWICE: the styled dialog, then a native `window.confirm` on top of
          it restating the same consequence in browser chrome. A confirmation the user has to
          give twice trains them to click through both. One dialog, which names the user and
          lists what is kept. */}
      <Modal open={!!deleteTarget} onClose={() => { if (!deleting) setDeleteTarget(null); }}
             title={deleteTarget ? `Delete user ${deleteTarget.username}?` : ''}>
        <p>Stops the agent, deletes workspace data, and marks the user deleted.</p>
        <p className="text-muted">Usage logs and billing history are kept for audit.</p>
        <div className="modal-footer">
          <button className="btn btn-secondary" disabled={deleting} onClick={() => setDeleteTarget(null)}>
            Cancel
          </button>
          <button className="btn btn-danger" disabled={deleting} onClick={() => handleDelete(false)}>
            {deleting ? <><span className="spinner-sm" /> Deleting…</> : 'Delete user'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Users;
